import { useEffect, useState } from "react";
import { CheckCircle2, Star, Trash2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDate } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import CriticalPinDialog from "../CriticalPinDialog";

const ST_TR = {
    pending: { label: "Onay Bekliyor", cls: "border-amber-200 bg-amber-100 text-amber-800" },
    approved: { label: "Yayında", cls: "border-emerald-200 bg-emerald-100 text-emerald-800" },
    rejected: { label: "Reddedildi", cls: "border-red-200 bg-red-100 text-red-700" },
};

export default function FeaturedTab() {
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState(null);
    const [pinTarget, setPinTarget] = useState(null);
    const load = () => api.get("/admin/featured").then((r) => setItems(r.data)).catch(() => setItems([]));
    useEffect(() => { load(); }, []);

    const review = async (f, status) => {
        setBusy(f.featured_id);
        try {
            await api.post(`/admin/featured/${f.featured_id}/review`, { status });
            toast.success(`${f.product_name} ${status === "approved" ? "yayına alındı" : "reddedildi"}`);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "İşlem başarısız");
        } finally {
            setBusy(null);
        }
    };

    const del = async (pin) => {
        try {
            await api.delete(`/admin/records/featured/${pinTarget.featured_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Talep kalıcı olarak silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        }
    };

    if (!items) return <div className="h-48 animate-pulse rounded-2xl bg-muted" />;

    return (
        <div data-testid="featured-tab">
            <p className="mb-4 text-sm text-muted-foreground">Tedarikçilerin kampanyalı ürün sayfasına çıkarma talepleri. Yalnızca aktif kampanyası olan ürünler onaylanabilir.</p>
            <div className="space-y-3">
                {items.map((f) => (
                    <div key={f.featured_id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-card p-4 shadow-sm" data-testid={`featured-row-${f.featured_id}`}>
                        <div className="flex items-center gap-3">
                            <Star className="h-5 w-5 fill-saffron-400 text-saffron-400" />
                            <div>
                                <p className="font-semibold">{f.product_name}</p>
                                <p className="text-xs text-muted-foreground">{fmtDate(f.created_at)}{f.reviewed_by ? ` • ${f.reviewed_by}` : ""}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-[10px] ${ST_TR[f.status]?.cls}`} data-testid={`featured-status-${f.featured_id}`}>{ST_TR[f.status]?.label}</Badge>
                            {f.status === "pending" && (
                                <>
                                    <Button size="sm" variant="outline" className="text-destructive" disabled={busy === f.featured_id} onClick={() => review(f, "rejected")} data-testid={`featured-reject-${f.featured_id}`}><XCircle className="mr-1 h-3.5 w-3.5" /> Reddet</Button>
                                    <Button size="sm" className="bg-emerald-700 text-white hover:bg-emerald-800" disabled={busy === f.featured_id} onClick={() => review(f, "approved")} data-testid={`featured-approve-${f.featured_id}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Onayla</Button>
                                </>
                            )}
                            <Button size="sm" variant="outline" className="text-destructive" disabled={busy === f.featured_id} onClick={() => setPinTarget(f)} data-testid={`featured-delete-${f.featured_id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </div>
                    </div>
                ))}
                {!items.length && <p className="rounded-2xl border border-dashed p-16 text-center text-sm text-muted-foreground" data-testid="featured-empty">Bekleyen talep yok.</p>}
            </div>
            <CriticalPinDialog open={!!pinTarget} onClose={() => setPinTarget(null)} onSubmit={del} actionLabel={pinTarget ? `Kampanya talebi silme: ${pinTarget.product_name}` : ""} />
        </div>
    );
}
